/*
 * Copyright (c) 2025 curryfirm.com
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import fetchUniqueLocationsLogged from './fetchUniqueLocationsLogged.js';
import { DROP_LOT_STREETS } from "../../constants.js";
import * as constants from '../../constants.js';

// Thunk to load unique locations logged from relevant Sheets document.
export const fetchUniqueLocationsLoggedThunk = createAsyncThunk(
    'locations/uniqueLocationsLogged',
    async (_, thunkAPI) => {
        const { columnMap, rows } = await fetchUniqueLocationsLogged();
        if (!(columnMap && rows && rows.length > 0)) {
            throw new Error(`${constants.UNIQUE_LOCATIONS_LOGGED_RANGE} did not load`);
        }
        return rows
            .map(row => {
                const streetAddress = row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.street]];
                const locId = row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.locationId]];
                const uniqueLocationKey = `${streetAddress}|${locId}`;
                return {
                    key: uniqueLocationKey,
                    customer: row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.customer]],
                    lat: row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.lat]],
                    lng: row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.lng]],
                    street: streetAddress,
                    city: row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.city]],
                    state: row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.state]],
                    zip: row[columnMap[constants.UNIQUE_LOCATIONS_LOGGED_COLUMN_TITLES.zip]],
                    locationId: locId,
                };
            });
    }
);

const uniqueLocationsLoggedSlice = createSlice({
    name: 'locations',
    initialState: {
        all: [],
        loading: false,
        error: null,
    },
    reducers: {
        replaceOrAddLocation(state, action) {
            const newLoc = action.payload;
            const idx = state.all.findIndex(loc => loc.key === newLoc.key);
            if (idx >= 0) {
                state.all[idx] = newLoc;
            }
            else {
                state.all.push(newLoc);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUniqueLocationsLoggedThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUniqueLocationsLoggedThunk.fulfilled, (state, action) => {
                state.loading = false;

                // Add fetched locations to store without blowing away ones put there already, e.g. by parsing URL parms
                const fetched = action.payload;
                const existing = state.all;
                const existingKeys = new Set(existing.map(l => l.key));
                state.all = [
                    ...existing,
                    ...fetched.filter(l => !existingKeys.has(l.key)),
                ];
            })
            .addCase(fetchUniqueLocationsLoggedThunk.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload?.message || // for thunks that use rejectWithValue
                    action.error?.message ||   // fallback for plain thrown errors
                    action.error ||            // fallback in case only a string is present
                    "Unknown error";
            });
    }
});

export const {
    replaceOrAddLocation,
} = uniqueLocationsLoggedSlice.actions;

export default uniqueLocationsLoggedSlice.reducer;

// Selectors
export const selectAllLocations = state => state.locations.all;
export const selectLocationsAreLoading = state => state.locations.loading;
export const selectLocationsError = state => state.locations.error;

// Define all drop-lot locations to be the first location that matches each defined drop-lot street.
export const selectDropLotLocations = createSelector(
    [selectAllLocations],
    (allLocs) => {
        const dropLots = DROP_LOT_STREETS
            .map(street =>
                allLocs.find(
                    loc => loc.street && loc.street.trim().toLowerCase().includes(street.trim().toLowerCase())
                )
            )
            .filter(Boolean);
        return dropLots;
    }
);
